import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Loader2, Building2, User, MapPin, CheckCircle } from 'lucide-react';
import { coreAPI, CompanyRegistrationData } from '../../lib/api';

interface RegistrationInfo {
  company_types: Array<{ value: string; label: string }>;
  site_types: Array<{ value: string; label: string }>;
  warehouse_types: Array<{ value: string; label: string }>;
}

export const CompanyRegistrationForm: React.FC = () => {
  const [currentStep, setCurrentStep] = useState('company');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registrationInfo, setRegistrationInfo] = useState<RegistrationInfo | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CompanyRegistrationData>();


  useEffect(() => {
    const fetchRegistrationInfo = async () => {
      try {
        const response = await coreAPI.getRegistrationInfo();
        setRegistrationInfo(response.data);
      } catch (err) {
        console.error('Failed to fetch registration info:', err);
      }
    };

    fetchRegistrationInfo();
  }, []);

  const onSubmit = async (data: CompanyRegistrationData) => {
    setIsLoading(true);
    setError(null);

    try {
      await coreAPI.registerCompany(data);
      setIsSuccess(true);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: Record<string, string | string[]> } };
      const data = axiosErr.response?.data;
      const nonFieldErrors = data?.['non_field_errors'];
      const errorMessage = (Array.isArray(nonFieldErrors) ? nonFieldErrors[0] : undefined) ||
        Object.values(data || {}).flat().join(', ') ||
        'Registration failed. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
            <h2 className="text-2xl font-bold text-green-700">Registration Successful!</h2>
            <p className="text-muted-foreground">
              Your company has been registered successfully. You can now log in to access the portal.
            </p>
            <Button onClick={() => window.location.href = '/login'}>
              Go to Login
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold">Register Your Company</h1>
        <p className="text-muted-foreground mt-2">
          Join the Helix Supplier Portal and connect with your business partners
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Tabs value={currentStep} onValueChange={setCurrentStep}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="company" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Company Info
              </TabsTrigger>
              <TabsTrigger value="user" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Admin User
              </TabsTrigger>
              <TabsTrigger value="address" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Address
              </TabsTrigger>
            </TabsList>

            <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <TabsContent value="company" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Company Name *</Label>
                    <Input
                      id="name"
                      {...register('name', { required: 'Company name is required' })}
                    />
                    {errors.name && (
                      <p className="text-sm text-red-600">{errors.name.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company_type">Company Type *</Label>
                    <Select
                      onValueChange={(value) => setValue('company_type', value as 'supplier' | 'customer')}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select company type" />
                      </SelectTrigger>
                      <SelectContent>
                        {registrationInfo?.company_types.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.company_type && (
                      <p className="text-sm text-red-600">{errors.company_type.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="primary_email">Primary Email *</Label>
                    <Input
                      id="primary_email"
                      type="email"
                      {...register('primary_email', { 
                        required: 'Primary email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address',
                        },
                      })}
                    />
                    {errors.primary_email && (
                      <p className="text-sm text-red-600">{errors.primary_email.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="primary_phone">Primary Phone</Label>
                    <Input
                      id="primary_phone"
                      {...register('primary_phone')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      type="url"
                      placeholder="https://example.com"
                      {...register('website')}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Company Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Brief description of your company..."
                    {...register('description')}
                  />
                </div>

                <div className="flex justify-end">
                  <Button type="button" onClick={() => setCurrentStep('user')}>
                    Next: Admin User
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="user" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="user_first_name">First Name *</Label>
                    <Input
                      id="user_first_name"
                      {...register('user_first_name', { required: 'First name is required' })}
                    />
                    {errors.user_first_name && (
                      <p className="text-sm text-red-600">{errors.user_first_name.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="user_last_name">Last Name *</Label>
                    <Input
                      id="user_last_name"
                      {...register('user_last_name', { required: 'Last name is required' })}
                    />
                    {errors.user_last_name && (
                      <p className="text-sm text-red-600">{errors.user_last_name.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="user_email">Email Address *</Label>
                    <Input
                      id="user_email"
                      type="email"
                      {...register('user_email', { 
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address',
                        },
                      })}
                    />
                    {errors.user_email && (
                      <p className="text-sm text-red-600">{errors.user_email.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="user_password">Password *</Label>
                    <Input
                      id="user_password"
                      type="password"
                      {...register('user_password', { 
                        required: 'Password is required',
                        minLength: {
                          value: 8,
                          message: 'Password must be at least 8 characters',
                        },
                      })}
                    />
                    {errors.user_password && (
                      <p className="text-sm text-red-600">{errors.user_password.message}</p>
                    )}
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="user_password_confirm">Confirm Password *</Label>
                    <Input
                      id="user_password_confirm"
                      type="password"
                      {...register('user_password_confirm', { 
                        required: 'Please confirm your password',
                        validate: (value) => {
                          const password = watch('user_password');
                          return value === password || 'Passwords do not match';
                        },
                      })}
                    />
                    {errors.user_password_confirm && (
                      <p className="text-sm text-red-600">{errors.user_password_confirm.message}</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button type="button" variant="outline" onClick={() => setCurrentStep('company')}>
                    Back
                  </Button>
                  <Button type="button" onClick={() => setCurrentStep('address')}>
                    Next: Address
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="address" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="address_line_1">Address Line 1</Label>
                    <Input
                      id="address_line_1"
                      {...register('address_line_1')}
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="address_line_2">Address Line 2</Label>
                    <Input
                      id="address_line_2"
                      {...register('address_line_2')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      {...register('city')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="state">State/Province</Label>
                    <Input
                      id="state"
                      {...register('state')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="postal_code">Postal Code</Label>
                    <Input
                      id="postal_code"
                      {...register('postal_code')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      {...register('country')}
                    />
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button type="button" variant="outline" onClick={() => setCurrentStep('user')}>
                    Back
                  </Button>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Registering...
                      </>
                    ) : (
                      'Complete Registration'
                    )}
                  </Button>
                </div>
              </TabsContent>
            </form>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
